import { useState, useEffect, useContext, useRef } from 'react';
import { searchWord, getSuggestions, DictionaryResponse } from '../services/dictionary';
import SearchBar from '../components/SearchBar';
import WordDefinition from '../components/WordDefinition';
import { UserContext } from '../store/useStore';
import { useLocation, useNavigate } from 'react-router-dom';
import { WholeWordIcon, LeafyGreen, Sparkles, Clock } from 'lucide-react';
import { translateFallback, translateText, translateMyMemory } from '../hooks/useTranslation';
import { AuthContext } from '../contexts/AuthContext';
import Modal from '../components/Modal';
import WordCard from '../components/WordCard';
import ExpressionForm from '../components/ExpressionForm';
import WordOfTheDayCard from '../components/WordOfTheDayCard';
import { getWordTranslationOverride } from '../data/wordNuances';
import { db } from '../lib/firebase';
import { ref, push, serverTimestamp, set, remove } from 'firebase/database';

export default function Vocabulary() {
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wordData, setWordData] = useState<DictionaryResponse | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [translatedWord, setTranslatedWord] = useState<string | null>(null);
  const [loginModal, setLoginModal] = useState(false);
  const [wordSaved, setWordSaved] = useState(false);
  const [showExpressionForm, setShowExpressionForm] = useState(false);
  const [failedWord, setFailedWord] = useState('');
  const [textToTranslate, setTextToTranslate] = useState<string>('');
  const { savedWords, getWordsPaginated, loadMore, loadingWords, addWord } = useContext(UserContext)
  const { currentUser } = useContext(AuthContext)
  const { state } = useLocation()
  const navigate = useNavigate();
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleCancelSearch = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsSearching(false);
  };

  const handleSearch = async (word: string) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsSearching(true);
    setError(null);
    setWordData(null);
    setShowExpressionForm(false);
    try {
      const wordToSearch = word.replace(/[^\w\s]/gi, '');
      const data = await searchWord(wordToSearch, abortController.signal);
      
      if (abortController.signal.aborted) return;
      
      // Format word to keep first letter capitalized and rest lowercase
      const formattedWord = wordToSearch.charAt(0).toUpperCase() + wordToSearch.slice(1).toLowerCase();
      data.word = formattedWord;
      
      setWordData(data);
      console.log('Word data:', data);
      setWordSaved(false)
      
      let currentTextToTranslate = wordToSearch.toLowerCase();
      if (wordToSearch.trim().split(' ').length > 1 || data.type === 'idiom') {
        const firstMeaning = data.meanings?.[0];
        if (firstMeaning?.definitions?.length > 0) {
          const firstDef = firstMeaning.definitions[0];
          currentTextToTranslate = typeof firstDef === 'string' ? firstDef : firstDef.definition;
        }
      }
      
      setTextToTranslate(currentTextToTranslate);
      
      const normalizedWord = data.word.toLowerCase().trim();
      const isSingleWordSearch = wordToSearch.trim().split(/\s+/).length === 1 && data.type !== 'idiom';
      const translationOverride = getWordTranslationOverride(normalizedWord);
      if (translationOverride) {
        setTranslatedWord(translationOverride);
        try {
          const translationRef = ref(db, `vocabulary/${normalizedWord}/translation`);
          await set(translationRef, translationOverride);
        } catch (e) {
          console.error(e);
        }
      } else if (data.translation && (!isSingleWordSearch || data.translation.includes('/') || data.translation.includes(','))) {
        const formattedTranslation = data.translation.charAt(0).toUpperCase() + data.translation.slice(1);
        setTranslatedWord(formattedTranslation);
      } else {
        translate(currentTextToTranslate, data.word);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log('Search aborted');
        return;
      }
      
      if (word.trim().split(' ').length > 1) {
        setFailedWord(word);
        setShowExpressionForm(true);
      } else {
        setError('Word not found. Please try another word.');
      }
      console.error('Error searching word:', err);
    } finally {
      if (abortControllerRef.current === abortController && !abortController.signal.aborted) {
        setIsSearching(false);
      }
    }
  };

  const handleInputChange = async (word: string) => {
    if (word.length > 1) {
      const newSuggestions = await getSuggestions(word);
      setSuggestions(newSuggestions);
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionSelect = (word: string) => {
    const wordWithoutPunctuation = word.replace(/[^\w\s]/gi, '');
    handleSearch(wordWithoutPunctuation);
  };

  const handleSave = async () => {
    if(!currentUser) {
      setLoginModal(true);
      return;
    }
    if (!wordData) return;

    setIsSaving(true);
    setError(null);
    
    try {
      addWord(wordData)
      setError('Word saved successfully!');
      setWordSaved(true)
    } catch (err) {
      console.error('Error saving word:', err);
      setError('Failed to save word. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const translate = async (text: string, originalWord: string) => {
    setTranslatedWord(null);
    try {
      const normalizedOriginal = (originalWord || text).toLowerCase().trim();
      const translationOverride = getWordTranslationOverride(normalizedOriginal);
      if (translationOverride) {
        setTranslatedWord(translationOverride);
        try {
          const translationRef = ref(db, `vocabulary/${normalizedOriginal}/translation`);
          await set(translationRef, translationOverride);
        } catch (e) {
          console.error(e);
        }
        return;
      }

      let translationResult = await translateText(text, 'en', 'pt');
      if (translationResult.toLowerCase().includes(text.toLowerCase())) {
        try {
          const fallbackResult = await translateFallback(text);
          translationResult = fallbackResult || translationResult;
        } catch (fallbackError) {
          try {
            const myMemoryResult = await translateMyMemory(text, 'en', 'pt');
            translationResult = myMemoryResult || translationResult;
          } catch (myMemoryError) {
            // Keep translationResult
          }
        }
      }
      
      let finalTranslation = translationResult || text;
      if (finalTranslation) {
        finalTranslation = finalTranslation.charAt(0).toUpperCase() + finalTranslation.slice(1);
      }
      setTranslatedWord(finalTranslation);
      
      // Save translation to RTDB cache
      if (originalWord) {
        try {
          const translationRef = ref(db, `vocabulary/${originalWord.toLowerCase().trim()}/translation`);
          await set(translationRef, finalTranslation);
        } catch (err) {
          console.error('Failed to cache translation in RTDB', err);
        }
      }
    } catch (error) {
      setTranslatedWord(text ? text.charAt(0).toUpperCase() + text.slice(1) : text);
    }
  }

  const handleFlagTranslation = async () => {
    if (!wordData || !translatedWord) return;
    
    // Save to Firebase Realtime Database
    try {
      const flaggedRef = ref(db, 'flagged_translations');
      await push(flaggedRef, {
        word: wordData.word,
        textToTranslate: textToTranslate,
        wrongTranslation: translatedWord,
        timestamp: serverTimestamp(),
        userId: currentUser?.uid || 'anonymous'
      });
      console.log('Flagged translation saved to database');
    } catch (err) {
      console.error('Error saving flagged translation:', err);
    }
    
    // Clear the cached translation from RTDB
    try {
      const translationRef = ref(db, `vocabulary/${wordData.word.toLowerCase().trim()}/translation`);
      await remove(translationRef);
      console.log('Translation removed from cache due to flag');
    } catch (err) {
      console.error('Error removing flagged translation from cache:', err);
    }

    // Attempt fallback translation
    try {
      setTranslatedWord('Loading fallback translation...');
      const fallbackResult = await translateFallback(textToTranslate);
      if (fallbackResult) {
        setTranslatedWord(fallbackResult.charAt(0).toUpperCase() + fallbackResult.slice(1));
      } else {
        setTranslatedWord(translatedWord); // Restore if empty
      }
    } catch (err) {
      console.error('Error fetching fallback translation:', err);
      setTranslatedWord(translatedWord); // Restore on error
    }
  };

  useEffect(() => {
    if (state && state.word) {
      handleSearch(state.word.replace(/[^\w\s]/gi, ''))
    }
  }, [state])

  // Get top 10 recent saved words
  const getRecentSavedWords = () => {
    if (!currentUser || savedWords.length === 0) return [];
    
    return [...savedWords]
      .sort((a, b) => {
        const aDate = a.lastPractice || a.createdAt || 0;
        const bDate = b.lastPractice || b.createdAt || 0;
        return bDate - aDate; // Most recent first
      })
      .slice(0, 10)
      .map(word => word.word);
  };

  const recentSavedWords = getRecentSavedWords();
  const recommendedWords = [
    'appreciate', 'challenge', 'opportunity', 'experience', 'knowledge', 
    'understand', 'communicate', 'develop', 'improve', 'achieve', 
    'success', 'effort', 'benefit', 'consider', 'recognize', 
    'significant', 'essential', 'effective', 'approach', 'strategy'
  ];

  // Component for Recommended Words and Recent Saved Words
  const RecommendedAndRecentSection = () => (
    <div className="mt-8">
      {/* Recommended Words */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <h2 className="text-lg font-semibold text-gray-800">Recommended Words</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {recommendedWords.map((word) => (
            <button
              key={word}
              onClick={() => handleSearch(word)}
              className="px-4 py-2 bg-white border border-purple-200 rounded-full text-sm font-medium text-purple-700 hover:bg-purple-50 hover:border-purple-300 transition-all shadow-sm hover:shadow-md"
            >
              {word}
            </button>
          ))}
        </div>
      </div>

      {/* Themes Link */}
      <div
        onClick={() => navigate('/themes')}
        className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-300 rounded-lg p-4 hover:bg-purple-100 hover:border-purple-400 transition-all cursor-pointer shadow-sm mb-6"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <LeafyGreen className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-800 mb-1">Learn Words by Theme</h3>
            <p className="text-sm text-gray-600">
              Explore vocabulary organized by themes like Travel, Restaurant, Hotel, and more. Perfect for structured learning!
            </p>
          </div>
          <div className="text-purple-600 font-semibold">→</div>
        </div>
      </div>

      {/* Recent Saved Words */}
      {currentUser && recentSavedWords.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-800">Your Recent Words</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {recentSavedWords.map((word) => (
              <button
                key={word}
                onClick={() => handleSearch(word)}
                className="px-4 py-2 bg-white border border-blue-200 rounded-full text-sm font-medium text-blue-700 hover:bg-blue-50 hover:border-blue-300 transition-all shadow-sm hover:shadow-md"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className='min-h-screen bg-gradient-to-br from-purple-50 to-white pb-16'>
      <Modal 
          isOpen={loginModal} 
          setIsModalOpen={setLoginModal} 
          title='Login or a Create Account'
          message='To save a word you must be logged in!'
          actionText='Login/Register' 
          action={() => navigate('/login')}
          />
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-8">
        <header className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <WholeWordIcon className="w-12 h-12 text-purple-600" />
              <h1 className="text-4xl font-bold text-gray-900">Word Search</h1>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Search Any Word and Learn!
            </p>
          </header>
          <SearchBar 
            onSearch={handleSearch} 
            isLoading={isSearching}
            suggestions={suggestions}
            onSuggestionSelect={handleSuggestionSelect}
            onInputChange={handleInputChange}
            wordParam={state?.word}
            onCancel={handleCancelSearch}
          />
          
          {/* Recommended Words Section - Show when no word is displayed */}
          {!wordData && <RecommendedAndRecentSection />}
        </div>

        {error && (
          <div className={`p-4 rounded-lg mb-6 ${error.includes('successfully!') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {error}
          </div>
        )}

        {showExpressionForm && (
          <ExpressionForm
            initialWord={failedWord}
            isSaving={isSaving}
            onCancel={() => setShowExpressionForm(false)}
            onSave={async (customData) => {
              if(!currentUser) {
                setLoginModal(true);
                return;
              }
              setIsSaving(true);
              try {
                await addWord(customData);
                setWordData(customData);
                setWordSaved(true);
                setShowExpressionForm(false);
                setError('Expression saved successfully!');
              } catch (err) {
                setError('Failed to save expression. Please try again.');
              } finally {
                setIsSaving(false);
              }
            }}
          />
        )}

        {wordData && !showExpressionForm && (
          <>
            <WordDefinition 
              data={wordData} 
              onSave={handleSave}
              isSaving={isSaving}
              translated={translatedWord || wordData.translation || ''}
              wordSaved={wordSaved}
              setWordSaved={setWordSaved}
              themeName={state?.themeName}
              setWordData={setWordData}
              onFlagTranslation={handleFlagTranslation}
            />
            <WordOfTheDayCard word={wordData.word} hideHeader={true} />
            {/* Recommended Words Section - Show after word definition */}
            <RecommendedAndRecentSection />
          </>
        )}

        {currentUser && savedWords.length > 0 && wordData === null && !showExpressionForm && (<div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 mt-6">Saved Words</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {savedWords.map((word) => (
                    <WordCard
                        key={word.word}
                        word={word.word}
                        lastPractice={word.lastPractice || word.createdAt}
                        progress={word.progress}
                        showDelete={true}
                        type={word.type}
                    />
                ))}
            </div>
            {loadMore && savedWords.length > 0 && (
                <button
                    onClick={() => getWordsPaginated(true)}
                    disabled={loadingWords}
                    className="mt-6 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                >
                    {loadingWords ? "Loading..." : "Load More"}
                </button>
            )}
        </div>)}
      </div>
    </div>
  );
}